/** @jsx onetext.jsx.element */

import type { OneText } from '../../../global';

// eslint-disable-next-line no-restricted-syntax
import { BrandBackground, BrandLogo } from '../components';
// eslint-disable-next-line no-restricted-syntax
import { defaultBrandConfig, SUPPRESSED_KEYWORDS, SUPPRESSED_QUERY_PARAMS, SUPPRESSED_ROUTES } from '../config';
// eslint-disable-next-line no-restricted-syntax
import { RayconBlueMagicCableGraphic, RayconMintMagicCableGraphic, RayconPurpleMagicCableGraphic } from '../images';

import { weekOneVariants, weekTwoVariants } from './variants';
import { weekThreevariants } from './variants/week3';

import type { GetPopupAPIResponse, Milliseconds, PopupConfig, PopupSpinnerConfig } from '@onetext/api';
import {
    BUTTON_LEVEL, BUTTON_SIZE, CLOSE_BUTTON_POSITION, COUNTRY, FONT_WEIGHT,
    POPUP_LAYOUT, QUERY_PARAM, SPINNER_ANIMATION
} from '@onetext/api';

declare let onetext : OneText;

const { clsx, deepmerge, useState } = onetext.jsx;
const { isPreviewMode, getURLQueryParam } = onetext.sdk;

export type MagicCableHeroImageProps = {
    shouldShowAnimation ?: boolean,
};

type ImageComponentType = (props : MagicCableHeroImageProps) => JSX.Element;

export type VariantType = {
    offerTextLine1 : string,
    offerTextLine2 : string,
    offerTextLine3 ?: string,
    smsPromptText : string,
    emailPromptText : string,
    smsButtonCtaText ?: string,
    teaserText : string,
    captcha ?: {
        titleText : string,
        images : Array<{
            url : string,
        }>,
    },
    showQuiz : boolean,
    claimOffer ?: {
        offerTextLine1 : string,
        offerTextLine2 : string,
        buttonCtaText : string,
    },
    customBackground ?: JSX.Element,
    landingPageUrlPathnames ?: Array<string>,
    heroImage ?: ImageComponentType | {
        url : string,
        alt : string,
    },
    propertyValues : Record<string, string>,
    externalID : string,
    showSpinTheWheel ?: boolean,
    spinTheWheelFinalPageText ?: string,
    config ?: PopupConfig,
};

export type Variants = Record<string, VariantType>;

const MagicCableHeroImage = ({ shouldShowAnimation } : MagicCableHeroImageProps) : JSX.Element => {

    return (
        <div className={
            clsx(
                'flex',
                'items-center',
                'justify-center',
                'h-full',
                'w-full',
                'relative',
                'px-5',
                'md:px-0',
                'md:overflow-visible',
                'p-2',
                'md:p-0',
                'md:-mr-24 mt-0'
            )
        }>
            <div className={
                clsx(
                    'flex flex-col',
                    'items-center',
                    'justify-center',
                    'md:h-auto',
                    'md:w-auto',
                    'h-auto',
                    'w-full',
                    'relative',
                    'overflow-visible',
                    'p-2',
                    'md:p-0',
                    'md:-mr-24 relative'
                )
            }>
                <img
                    src={ RayconMintMagicCableGraphic }
                    alt={ 'Mint Raycon Magic Cable' }
                    style={
                        {
                            animationDelay: '0.3s',
                            willChange:     'opacity, transform'
                        }
                    }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'md:scale-115',
                            'object-contain',
                            'object-center',
                            'duration-300',
                            'absolute md:right-24 -top-28',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable animation-fill-mode-both'
                                )
                                : undefined
                        )
                    }
                />
                <img
                    src={ RayconBlueMagicCableGraphic }
                    alt={ 'Blue Raycon Magic Cable' }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'object-contain',
                            'object-center',
                            'duration-300 z-10',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable'
                                )
                                : undefined
                        )
                    }
                    style={
                        {
                            animationDelay: '0.5s',
                            willChange:     'opacity, transform'
                        }
                    }
                />
                <img
                    src={ RayconPurpleMagicCableGraphic }
                    alt={ 'Purple Raycon Magic Cable' }
                    className={
                        clsx(
                            'md:h-auto',
                            'h-auto',
                            'max-h-[140px] md:max-h-[240px]',
                            'w-auto',
                            'max-w-full',
                            'object-contain',
                            'object-center',
                            'duration-300',
                            'absolute md:right-24 top-28 z-10',
                            'masked-element-bottom-half-gradient',
                            shouldShowAnimation
                                ? clsx(
                                    'transition-transform',
                                    'fade-in-up-raycon-cable'
                                )
                                : undefined
                        )
                    }
                    style={
                        {
                            animationDelay: '0.7s',
                            willChange:     'opacity, transform'
                        }
                    }
                />

            </div>
        </div>
    );
};

const spinnerConfig : PopupSpinnerConfig = {
    element: {
        arrow: {
            style: {
                background: {
                    base: '#ffffff'
                }
            }
        },
        hubcap: {
            style: {
                background: {
                    base: '#1E201F'
                },
                border: {
                    base: 'rgba(56,49,255,1)'
                }
            }
        },
        wheel: {
            style: {
                background: {
                    base: 'rgba(56,49,255,1)'
                },
                border: {
                    base: 'rgba(56,49,255,1)'
                }
            }
        }
    },
    options: [
        {
            label: '60% off',
            color: {
                background: {
                    base: 'rgba(56,49,255,1)'
                },
                text: {
                    base: '#ffffff'
                }
            }
        },
        {
            label: 'Free gift',
            color: {
                background: {
                    base: '#ffffff'
                },
                text: {
                    base: '#1E201F'
                }
            }
        },
        {
            label: '10% off',
            color: {
                background: {
                    base: 'rgba(56,49,255,1)'
                },
                text: {
                    base: '#ffffff'
                }
            }
        },
        {
            label: '20% off',
            color: {
                background: {
                    base: '#ffffff'
                },
                text: {
                    base: '#1E201F'
                }
            }
        }
    ]
};

/**
 * This object and schema is temporary until we finalize variant configs in db
 */

// 4:30 PM PM March 26, 2025 (New York time)
export const START_DATE = new Date(Date.UTC(2025, 2, 31, 15, 0, 0)).getTime();

const variants : Variants = {
    ...weekOneVariants,
    ...weekTwoVariants,
    ...weekThreevariants,
    SPIN_CABLE_DISCOUNT_BRIGHT_COLOR_V2: {
        offerTextLine1:          'Up to 60% off',
        offerTextLine2:          'Magic Spin Cables',
        emailPromptText:         'Enter your email to claim your special offer',
        smsPromptText:           'with texts to claim up to 60% off Magic Spin Cables',
        teaserText:              'UP TO 60% OFF',
        showQuiz:                false,
        heroImage:               MagicCableHeroImage,
        landingPageUrlPathnames: [
            '/pages/magic-spin-cable-purple-hm',
            '/pages/magic-spin-cable-blue',
            '/pages/magic-spin-cable-mint',
            '/pages/magic-spin-cable-adr5'
        ],
        propertyValues: {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'SPIN_DISCOUNT_BRIGHT_COLOR',
            'OT - Landing Page': 'DEFAULT'
        },
        config: {
            style: {
                color: {
                    background: 'rgba(3,0,86,1)'
                }
            },
            component: {
                button: {
                    level: {
                        primary: {
                            style: {
                                text: {
                                    base:  '#000',
                                    hover: '#000'
                                },
                                background: {
                                    base:  'rgba(255, 255, 255,1)',
                                    hover: 'rgba(255, 255, 255,0.9)'
                                }
                            }
                        }
                    }
                }
            }
        },
        externalID: 'spin_cable_bright_color_v2'
    },
    SPIN_CABLE_DISCOUNT_SPINNER: {
        offerTextLine1:          'SPIN TO WIN',
        offerTextLine2:          'Magic Spin Cables',
        emailPromptText:         'Enter your email to stop the wheel',
        smsPromptText:           'Finish signing up with text to stop the wheel',
        teaserText:              'Spin to Win',
        smsButtonCtaText:        'Stop the Wheel',
        showQuiz:                false,
        landingPageUrlPathnames: [
            '/pages/magic-spin-cable-purple-hm',
            '/pages/magic-spin-cable-blue',
            '/pages/magic-spin-cable-mint',
            '/pages/magic-spin-cable-adr5'
        ],
        propertyValues: {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'SPIN_DISCOUNT_SPINNER',
            'OT - Landing Page': 'DEFAULT'
        },
        config: {
            component: {
                spinner: spinnerConfig
            }
        },
        showSpinTheWheel:          true,
        spinTheWheelFinalPageText: 'Up to 60% off',
        externalID:                'spin_cable_spin_to_win'
    },
    // Open Earbuds variants (Meta)
    OPEN_EARBUDS_DISCOUNT_V4: {
        offerTextLine1:          'Lock In 20% off',
        offerTextLine2:          'Open Earbuds',
        emailPromptText:         'Enter your email to claim your special offer',
        smsPromptText:           'with texts to claim 20% off Open Earbuds',
        teaserText:              'GET 20% OFF',
        showQuiz:                false,
        landingPageUrlPathnames: [ '/pages/raycon-open-earbuds-adr4' ],
        heroImage:               {
            url: 'https://static.onetext.com/misc/open-earbuds-1200x1200.png',
            alt: 'Open Earbuds'
        },
        propertyValues: {
            'OT - Campaign':     'METAO15',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'OPEN_DISCOUNT',
            'OT - Landing Page': 'DEFAULT'
        },
        externalID: 'open_discount_v4'
    },
    OPEN_EARBUDS_DISCOUNT_SPINNER: {
        offerTextLine1:          'SPIN TO WIN',
        offerTextLine2:          'Open Earbuds',
        emailPromptText:         'Enter your email to stop the wheel',
        smsPromptText:           'Finish signing up with text to stop the wheel',
        teaserText:              'Spin to Win',
        smsButtonCtaText:        'Stop the Wheel',
        showQuiz:                false,
        landingPageUrlPathnames: [ '/pages/raycon-open-earbuds-adr4' ],
        propertyValues:          {
            'OT - Campaign':     'APPLOVINSPIN',
            'OT - Test':         'DISCOUNT',
            'OT - Variant':      'OPEN_DISCOUNT_SPINNER',
            'OT - Landing Page': 'DEFAULT'
        },
        config: {
            component: {
                spinner: spinnerConfig
            }
        },
        showSpinTheWheel:          true,
        spinTheWheelFinalPageText: 'Up to 20% off',
        externalID:                'open_discount_spin_to_win'
    }
};

export type VariantKey = keyof typeof variants;

const getPathname = (url : string) : string => {
    try {
        return new URL(url).pathname;
    } catch {
        return url;
    }
};

const getPreviewVariant = () : VariantType | undefined => {
    const popupIdInQuery = getURLQueryParam(QUERY_PARAM.POPUP_ID);

    if (popupIdInQuery) {
        // popup variants across different weeks.
        const combinedVariants = Object.entries(variants);
        const variant = combinedVariants.find(
            ([ , vnt ]) => vnt.externalID === popupIdInQuery
        );

        if (variant) {
            return variant[1];
        }
    }
};

const selectMatchingVariant = (popup : GetPopupAPIResponse) : VariantType | undefined => {
    const variantEntries = Object.entries(variants);

    for (const [ , variant ] of variantEntries) {
        if (variant.externalID === popup.externalID) {
            return variant;
        }
    }
};

const {
    Popup,
    Page,
    EmailField,
    PhoneField,
    Button,
    Row,
    Column,
    Columns,
    Quiz,
    QuizOption,
    Terms,
    Teaser,
    PopupContainer,
    Captcha,
    Spinner
} = onetext.component.consent.popup;

// Define the props type for HeroImage
type HeroImageProps = {
    imageUrl : string,
    alt : string,
};

// Hero image component for two-column layout
const HeroImage = ({ imageUrl, alt } : HeroImageProps) : JSX.Element => {
    // Determine if it's a large image - brittle and needs to be updated
    const isLargeImage = imageUrl.includes('1200x1200');

    return (
        <div className={
            clsx(
                'flex',
                'items-center',
                'justify-center',
                'h-full',
                'w-full',
                'relative',
                'px-5',
                'md:px-0',
                'md:overflow-visible',
                'p-2',
                'md:p-0',
                'md:-mr-24'
            )
        }>
            <div className={
                clsx(
                    'flex',
                    'items-center',
                    'justify-center',
                    'md:h-auto',
                    'md:w-auto',
                    'h-auto',
                    'w-full',
                    'relative',
                    'overflow-visible',
                    'p-2',
                    'md:p-0',
                    'md:-mr-24'
                )
            }>
                <img
                    src={ imageUrl }
                    alt={ alt }
                    className={
                        clsx(
                            'md:h-auto',
                            isLargeImage
                                ? 'md:min-h-[600px]'
                                : 'md:min-h-[560px]',
                            'h-auto',
                            'max-h-[320px]',
                            'w-auto',
                            'max-w-full',
                            isLargeImage
                                ? 'md:max-w-[600px]'
                                : 'md:max-w-[650px]',
                            isLargeImage
                                ? 'md:scale-105'
                                : 'md:scale-115',
                            'object-contain',
                            'object-center',
                            'transition-transform',
                            'duration-300'
                        )
                    }
                />
            </div>
        </div>
    );
};

export const QUIZ_PROPERTY_NAME = 'Amped - Interest';

export enum SHOPPING_INTENT {
    EARBUDS = 'EARBUDS',
    HEADPHONES = 'HEADPHONES',
    OPEN_AUDIO = 'OPENAUDIO',
    FAST_CHARGE_TECH = 'FASTCHARGETECH'
}

type StandardPopupProps = {
    popup : GetPopupAPIResponse,
};

export const StandardPopup = ({
    popup
} : StandardPopupProps) : JSX.Element | null => {
    // Select the appropriate variant based on current URL
    const currentUrl = typeof window === 'undefined'
        ? ''
        : window.location.href;

    const variant = isPreviewMode()
        ? getPreviewVariant()
        : selectMatchingVariant(popup);

    const [ isFinalSpin, setIsFinalSpin ] = useState(false);

    if (!variant) {
        return null;
    }

    let brandConfig = defaultBrandConfig;

    if (variant.config) {
        brandConfig = deepmerge(defaultBrandConfig, variant.config);
    }

    const hasHeroImage = Boolean(variant.heroImage);

    return (
        <PopupContainer
            popup={ { ...popup, config: brandConfig } }
            delay={ 500 as Milliseconds }
            suppressedRoutes={ SUPPRESSED_ROUTES }
            suppressedKeywords={ SUPPRESSED_KEYWORDS }
            suppressedQueryParams={ SUPPRESSED_QUERY_PARAMS }
            shownToCountries={ [ COUNTRY.US ] }
            initialProperties={
                {
                    ...variant.propertyValues,
                    'OT - URL': getPathname(currentUrl).slice(0, 100)
                }
            }
        >
            <Popup
                layout={ POPUP_LAYOUT.FULLSCREEN }
                overlayClassName={ 'bg-black bg-opacity-90' }
                background={
                    variant.customBackground
                        ? variant.customBackground
                        : hasHeroImage
                            ? undefined
                            : <BrandBackground />
                }>

                { /* Spinner Page - Only shown if variant.showSpinTheWheen is true */ }
                {
                    variant.showSpinTheWheel
                        ? (
                            <Page openKeyboardOnSubmit>
                                <Row>
                                    <Columns className={ 'md:gap-16 gap-0' }>
                                        <Column className={ 'pb-0 md:pb-14' }>
                                            <div className={ 'mb-10' }>
                                                <BrandLogo />
                                            </div>
                                            <p className={
                                                clsx(
                                                    'text-4xl md:text-5xl',
                                                    'uppercase tracking-wider',
                                                    'text-center font-semibold mb-10'
                                                )
                                            }>
                                                { 'Spin to Win' }
                                            </p>
                                            <div className={ 'w-[264px] flex flex-col items-center gap-2.5' }>
                                                <Button
                                                    size={ BUTTON_SIZE.LARGE }>
                                                    { 'Try my luck' }
                                                </Button>
                                                <Button
                                                    level={ BUTTON_LEVEL.SECONDARY }
                                                    size={ BUTTON_SIZE.SMALL }
                                                    closePopup>
                                                    { 'No, thanks' }
                                                </Button>
                                            </div>
                                        </Column>
                                        <Column className={ 'max-w-96 hidden md:flex' }>
                                            <Spinner
                                                overrideStyles={
                                                    {
                                                        mobile: {
                                                            fontWeight: FONT_WEIGHT.BOLD,
                                                            fontSize:   '20px'
                                                        }
                                                    }
                                                }
                                                animation={ SPINNER_ANIMATION.SPIN_TEASE }
                                            />
                                        </Column>
                                    </Columns>
                                </Row>
                                <div
                                    className={
                                        clsx(
                                            'block md:hidden',
                                            'absolute bottom-[-140px]',
                                            'left-1/2 transform -translate-x-1/2'
                                        )
                                    }
                                >
                                    <Spinner
                                        overrideStyles={
                                            {
                                                mobile: {
                                                    fontWeight: FONT_WEIGHT.BOLD,
                                                    fontSize:   '20px'
                                                }
                                            }
                                        }
                                        animation={ SPINNER_ANIMATION.SPIN_TEASE } />
                                </div>
                            </Page>
                        )
                        : null
                }

                { /* Quiz Page - Only shown if variant.showQuiz is true */ }
                {
                    variant.showQuiz
                        ? (
                            <Page
                                className={ 'pb-0 md:pb-0 relative' }
                                openKeyboardOnSubmit>
                                <Row>
                                    <Columns className={ 'md:overflow-visible md:gap-16 gap-0' }>
                                        { /* First column for content */ }
                                        <Column className={
                                            hasHeroImage
                                                ? 'md:pb-0 z-20 relative'
                                                : 'relative z-20'
                                        }>
                                            <div className={ 'mb-[34px] md:mb-[44px]' }>
                                                <BrandLogo />
                                            </div>
                                            <p className={
                                                clsx(
                                                    'text-[24px] md:text-[36px] mb-[4px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold'
                                                )
                                            }>
                                                { variant.offerTextLine1 }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[38px] md:text-[56px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold',
                                                    'mb-[16px]'
                                                )
                                            }>
                                                { variant.offerTextLine2 }
                                            </p>
                                            {
                                                variant.offerTextLine3
                                                    ? (
                                                        <p className={
                                                            clsx(
                                                                'text-[24px] md:text-[36px] mb-[4px]',
                                                                'leading-[1.1em]',
                                                                'uppercase',
                                                                'text-center',
                                                                'font-bold'
                                                            )
                                                        }>
                                                            { variant.offerTextLine3 }
                                                        </p>
                                                    )
                                                    : null
                                            }
                                            <p className={
                                                clsx(
                                                    'text-[16px] md:text-[20px]',
                                                    'font-light',
                                                    'leading-[1.2em]',
                                                    'text-center'
                                                )
                                            }>
                                                { 'To claim your offer, tell us' }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[16px] md:text-[20px]',
                                                    'font-light',
                                                    'leading-[1.2em]',
                                                    'text-center'
                                                )
                                            }>
                                                { 'what you\'re looking for:' }
                                            </p>
                                            <Quiz propertyName={ QUIZ_PROPERTY_NAME }>
                                                <div className={
                                                    clsx(
                                                        'w-[264px]',
                                                        'flex',
                                                        'flex-col',
                                                        'items-center',
                                                        'gap-3',
                                                        'mt-[26px]'
                                                    )
                                                }>
                                                    <QuizOption propertyValue={ SHOPPING_INTENT.EARBUDS }>
                                                        <Button>
                                                            { 'Earbuds' }
                                                        </Button>
                                                    </QuizOption>
                                                    <QuizOption propertyValue={ SHOPPING_INTENT.HEADPHONES }>
                                                        <Button>
                                                            { 'Headphones' }
                                                        </Button>
                                                    </QuizOption>
                                                    <QuizOption propertyValue={ SHOPPING_INTENT.OPEN_AUDIO }>
                                                        <Button>
                                                            { 'Open Audio' }
                                                        </Button>
                                                    </QuizOption>
                                                    <QuizOption propertyValue={ SHOPPING_INTENT.FAST_CHARGE_TECH }>
                                                        <Button>
                                                            { 'Fast-Charge Tech' }
                                                        </Button>
                                                    </QuizOption>
                                                </div>
                                            </Quiz>
                                        </Column>

                                        { /* Second column for hero image - positioned at bottom on mobile */ }
                                        {
                                            variant.heroImage
                                                ? (
                                                    <Column className={
                                                        clsx(
                                                            'md:flex top-44 bottom-0 left-0 right-0',
                                                            'flex justify-center w-full md:w-auto z-10 max-w-96'
                                                        )
                                                    }>
                                                        {
                                                            typeof variant.heroImage === 'function'
                                                                ? (
                                                                    <variant.heroImage shouldShowAnimation={ true } />
                                                                )
                                                                : (
                                                                    <HeroImage
                                                                        imageUrl={ variant.heroImage.url }
                                                                        alt={ variant.heroImage.alt }
                                                                    />
                                                                )
                                                        }
                                                    </Column>
                                                )
                                                : null
                                        }
                                        {
                                            variant.showSpinTheWheel
                                                ? (
                                                    <div className={ 'max-w-96 hidden md:flex' }>
                                                        <Spinner
                                                            overrideStyles={
                                                                {
                                                                    mobile: {
                                                                        fontWeight: FONT_WEIGHT.BOLD,
                                                                        fontSize:   '20px'
                                                                    }
                                                                }
                                                            }
                                                            animation={ SPINNER_ANIMATION.SPINNING }
                                                        />
                                                    </div>
                                                )
                                                : null
                                        }
                                    </Columns>
                                    {
                                        variant.showSpinTheWheel
                                            ? (
                                                <div
                                                    className={
                                                        clsx(
                                                            'block md:hidden',
                                                            'absolute bottom-[-140px]',
                                                            'left-1/2 transform -translate-x-1/2'
                                                        )
                                                    }
                                                >
                                                    <Spinner
                                                        overrideStyles={
                                                            {
                                                                mobile: {
                                                                    fontWeight: FONT_WEIGHT.BOLD,
                                                                    fontSize:   '20px'
                                                                }
                                                            }
                                                        }
                                                        animation={ SPINNER_ANIMATION.SPINNING } />
                                                </div>
                                            )
                                            : null
                                    }
                                </Row>
                            </Page>
                        )
                        : null
                }

                { /* Claim Offer Page - Only shown if variant.claimOffer object is present */ }
                {
                    variant.claimOffer
                        ? (
                            <Page
                                className={ 'pb-0 md:pb-0 relative' }
                                openKeyboardOnSubmit>
                                <Row>
                                    <Columns className={ 'md:overflow-visible md:gap-16 gap-0' }>
                                        { /* First column for content */ }
                                        <Column className={
                                            hasHeroImage
                                                ? 'md:pb-0 z-20 relative'
                                                : 'relative z-20'
                                        }>
                                            <div className={ 'mb-[34px] md:mb-[44px]' }>
                                                <BrandLogo />
                                            </div>
                                            <p className={
                                                clsx(
                                                    'text-[24px] md:text-[36px] mb-[4px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold'
                                                )
                                            }>
                                                { variant.claimOffer.offerTextLine1 }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[38px] md:text-[64px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold',
                                                    'mb-[16px]'
                                                )
                                            }>
                                                { variant.claimOffer.offerTextLine2 }
                                            </p>
                                            {
                                                variant.offerTextLine3
                                                    ? (
                                                        <p className={
                                                            clsx(
                                                                'text-[24px] md:text-[36px] mb-[4px]',
                                                                'leading-[1.1em]',
                                                                'uppercase',
                                                                'text-center',
                                                                'font-bold'
                                                            )
                                                        }>
                                                            { variant.offerTextLine3 }
                                                        </p>
                                                    )
                                                    : null
                                            }
                                            <div className={
                                                clsx(
                                                    'w-[300px]'
                                                )
                                            }>
                                                <Button>
                                                    { 'Claim Offer' }
                                                </Button>
                                            </div>
                                        </Column>

                                        { /* Second column for hero image - positioned at bottom on mobile */ }
                                        {
                                            variant.heroImage
                                                ? (
                                                    <Column className={
                                                        clsx(
                                                            'md:flex bottom-0 top-44 left-0 right-0',
                                                            'flex justify-center z-10 w-96'
                                                        )
                                                    }>
                                                        {
                                                            typeof variant.heroImage === 'function'
                                                                ? (
                                                                    <variant.heroImage shouldShowAnimation={ true } />
                                                                )
                                                                : (
                                                                    <HeroImage
                                                                        imageUrl={ variant.heroImage.url }
                                                                        alt={ variant.heroImage.alt }
                                                                    />
                                                                )
                                                        }
                                                    </Column>
                                                )
                                                : null
                                        }
                                        {
                                            variant.showSpinTheWheel
                                                ? (
                                                    <div className={ 'max-w-96 hidden md:flex' }>
                                                        <Spinner
                                                            overrideStyles={
                                                                {
                                                                    mobile: {
                                                                        fontWeight: FONT_WEIGHT.BOLD,
                                                                        fontSize:   '20px'
                                                                    }
                                                                }
                                                            }
                                                            animation={ SPINNER_ANIMATION.SPINNING }
                                                        />
                                                    </div>
                                                )
                                                : null
                                        }
                                    </Columns>
                                </Row>
                                {
                                    variant.showSpinTheWheel
                                        ? (
                                            <div
                                                className={
                                                    clsx(
                                                        'block md:hidden',
                                                        'absolute bottom-[-140px]',
                                                        'left-1/2 transform -translate-x-1/2'
                                                    )
                                                }
                                            >
                                                <Spinner
                                                    overrideStyles={
                                                        {
                                                            mobile: {
                                                                fontWeight: FONT_WEIGHT.BOLD,
                                                                fontSize:   '20px'
                                                            }
                                                        }
                                                    }
                                                    animation={ SPINNER_ANIMATION.SPINNING } />
                                            </div>
                                        )
                                        : null
                                }
                            </Page>
                        )
                        : null
                }

                { /* Captcha Page - Only shown if variant.showCaptcha is true */ }
                {
                    variant.captcha
                        ? (
                            <Page
                                className={ 'pb-0 md:pb-0 relative' }
                                openKeyboardOnSubmit>
                                <Row>
                                    <Columns className={ 'md:overflow-visible' }>
                                        { /* First column for content */ }
                                        <Column className={
                                            'relative z-20 px-4 md:px-0'
                                        }>
                                            <div className={ 'mb-[34px] md:mb-[44px]' }>
                                                <BrandLogo />
                                            </div>
                                            <p className={
                                                clsx(
                                                    'text-[24px] md:text-[36px] mb-[4px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold'
                                                )
                                            }>
                                                { variant.offerTextLine1 }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[38px] md:text-[56px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold',
                                                    'mb-[16px]'
                                                )
                                            }>
                                                { variant.offerTextLine2 }
                                            </p>
                                            {
                                                variant.offerTextLine3
                                                    ? (
                                                        <p className={
                                                            clsx(
                                                                'text-[24px] md:text-[36px] mb-[4px]',
                                                                'leading-[1.1em]',
                                                                'uppercase',
                                                                'text-center',
                                                                'font-bold'
                                                            )
                                                        }>
                                                            { variant.offerTextLine3 }
                                                        </p>
                                                    )
                                                    : null
                                            }
                                            <p className={
                                                clsx(
                                                    'text-[16px] md:text-[20px]',
                                                    'font-light mb-3',
                                                    'leading-[1.2em]',
                                                    'text-center'
                                                )
                                            }>
                                                { variant.captcha.titleText }
                                            </p>
                                            <Captcha images={ variant.captcha.images } />
                                            <Button className={ 'mt-6 max-w-[300px]' }>
                                                { 'Submit' }
                                            </Button>
                                        </Column>
                                    </Columns>
                                </Row>
                            </Page>
                        )
                        : null
                }

                { /* Email Page - Always shown */ }
                <Page
                    className={ 'pb-0 md:pb-0 relative' }
                    openKeyboardOnSubmit>
                    <Row>
                        <Columns className={ 'md:overflow-visible md:gap-16 gap-0' }>
                            <Column className={
                                hasHeroImage
                                    ? 'md:pb-0 z-20 relative'
                                    : 'relative z-20'
                            }>
                                <div className={ 'mb-[34px] md:mb-[44px]' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-[24px] md:text-[36px] mb-[4px]',
                                        'leading-[1.1em]',
                                        'uppercase',
                                        'text-center',
                                        'font-semibold'
                                    )
                                }>
                                    { variant.offerTextLine1 }
                                </p>
                                <p className={
                                    clsx(
                                        'text-[44px] md:text-[64px]',
                                        'leading-[1.1em]',
                                        'uppercase',
                                        'text-center',
                                        'font-semibold'
                                    )
                                }>
                                    { variant.offerTextLine2 }
                                </p>
                                {
                                    variant.offerTextLine3
                                        ? (
                                            <p className={
                                                clsx(
                                                    'text-[24px] md:text-[36px] mb-[4px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-bold'
                                                )
                                            }>
                                                { variant.offerTextLine3 }
                                            </p>
                                        )
                                        : null
                                }
                                <p className={
                                    clsx(
                                        'text-[16px] md:text-[20px]',
                                        'font-light',
                                        'leading-[1.2em]',
                                        'text-center',
                                        'mb-6'
                                    )
                                }>
                                    { variant.emailPromptText }
                                </p>
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex',
                                        'flex-col',
                                        'items-center',
                                        'gap-2.5'
                                    )
                                }>
                                    <EmailField autofocus={ true } />
                                    <Button>
                                        { 'Continue' }
                                    </Button>
                                </div>
                            </Column>

                            { /* Second column for hero image - positioned at bottom on mobile */ }
                            {
                                variant.heroImage
                                    ? (
                                        <Column className={
                                            clsx(
                                                'flex justify-center w-96'
                                            )
                                        }>
                                            {
                                                typeof variant.heroImage === 'function'
                                                    ? (
                                                        <variant.heroImage shouldShowAnimation={
                                                            (
                                                                !variant.showQuiz &&
                                                                !variant.claimOffer
                                                            )
                                                        } />
                                                    )
                                                    : (
                                                        <HeroImage
                                                            imageUrl={ variant.heroImage.url }
                                                            alt={ variant.heroImage.alt }
                                                        />
                                                    )
                                            }
                                        </Column>
                                    )
                                    : null
                            }
                            {
                                variant.showSpinTheWheel
                                    ? (
                                        <div className={ 'max-w-96 hidden md:flex' }>
                                            <Spinner
                                                overrideStyles={
                                                    {
                                                        mobile: {
                                                            fontWeight: FONT_WEIGHT.BOLD,
                                                            fontSize:   '20px'
                                                        }
                                                    }
                                                }
                                                animation={ SPINNER_ANIMATION.SPINNING }
                                            />
                                        </div>
                                    )
                                    : null
                            }
                        </Columns>
                    </Row>
                    {
                        variant.showSpinTheWheel
                            ? (
                                <div
                                    className={
                                        clsx(
                                            'block md:hidden',
                                            'absolute bottom-[-140px]',
                                            'left-1/2 transform -translate-x-1/2'
                                        )
                                    }
                                >
                                    <Spinner
                                        overrideStyles={
                                            {
                                                mobile: {
                                                    fontWeight: FONT_WEIGHT.BOLD,
                                                    fontSize:   '20px'
                                                }
                                            }
                                        }
                                        animation={ SPINNER_ANIMATION.SPINNING } />
                                </div>
                            )
                            : null
                    }
                </Page>

                { /* SMS Page - Always shown */ }
                <Page
                    className={ 'pb-0 md:pb-0 relative' }
                    submitDelay={
                        variant.showSpinTheWheel
                            ? 5000 as Milliseconds
                            : undefined
                    }
                    onSubmitStart={
                        variant.showSpinTheWheel
                            ? () => setIsFinalSpin(true)
                            : undefined
                    } >
                    <Row>
                        <Columns className={ 'md:overflow-visible' }>
                            <Column>
                                <div className={ 'mb-[34px] md:mb-[44px]' }>
                                    <BrandLogo />
                                </div>
                                <p className={
                                    clsx(
                                        'text-[32px] md:text-[44px]',
                                        'font-semibold',
                                        'uppercase',
                                        'tracking-wider',
                                        'text-center',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { 'Finish signing up' }
                                </p>
                                <p className={
                                    clsx(
                                        'text-md',
                                        'font-light',
                                        'leading-relaxed',
                                        'text-center',
                                        'mb-6',
                                        'md:whitespace-nowrap'
                                    )
                                }>
                                    { variant.smsPromptText }
                                </p>
                                <Terms
                                    brandName={ 'Raycon' }
                                    termsUrl={ 'https://rayconglobal.com/pages/terms-of-service' }
                                    privacyUrl={ 'https://rayconglobal.com/pages/privacy-policy' }
                                />
                                <div className={
                                    clsx(
                                        'w-[264px]',
                                        'flex',
                                        'flex-col',
                                        'items-center',
                                        'gap-2.5',
                                        'mt-6'
                                    )
                                }>
                                    <PhoneField autofocus={ true } />
                                    <Button>
                                        {
                                            variant.smsButtonCtaText
                                                ? variant.smsButtonCtaText
                                                : 'Activate Offer'
                                        }
                                    </Button>
                                </div>
                            </Column>

                            { /* Second column for hero image - positioned at bottom on mobile */ }
                            {
                                variant.heroImage
                                    ? (
                                        <Column className={
                                            clsx(
                                                'flex justify-center w-96'
                                            )
                                        }>
                                            {
                                                typeof variant.heroImage === 'function'
                                                    ? (
                                                        <variant.heroImage />
                                                    )
                                                    : (
                                                        <HeroImage
                                                            imageUrl={ variant.heroImage.url }
                                                            alt={ variant.heroImage.alt }
                                                        />
                                                    )
                                            }
                                        </Column>
                                    )
                                    : null
                            }
                            {
                                variant.showSpinTheWheel
                                    ? (

                                        <div className={ 'max-w-96 hidden md:flex' }>
                                            <Spinner
                                                overrideStyles={
                                                    {
                                                        mobile: {
                                                            fontWeight: FONT_WEIGHT.BOLD,
                                                            fontSize:   '20px'
                                                        }
                                                    }
                                                }
                                                animation={
                                                    isFinalSpin
                                                        ? SPINNER_ANIMATION.SPIN_FINAL
                                                        : SPINNER_ANIMATION.SPINNING
                                                }
                                            />
                                        </div>
                                    )
                                    : null
                            }
                        </Columns>
                    </Row>
                    {
                        variant.showSpinTheWheel
                            ? (
                                <div
                                    className={
                                        clsx(
                                            'block md:hidden',
                                            'absolute bottom-[-140px]',
                                            'left-1/2 transform -translate-x-1/2'
                                        )
                                    }
                                >
                                    <Spinner
                                        overrideStyles={
                                            {
                                                mobile: {
                                                    fontWeight: FONT_WEIGHT.BOLD,
                                                    fontSize:   '20px'
                                                }
                                            }
                                        }
                                        animation={ SPINNER_ANIMATION.SPINNING } />
                                </div>
                            )
                            : null
                    }
                </Page>

                { /* Success Page - Always shown */ }
                <Page className={ 'pb-0 md:pb-0 relative' }>
                    <Row>
                        <Columns className={ 'md:overflow-visible' }>
                            {
                                variant.showSpinTheWheel
                                    ? (
                                        <Column >
                                            <div className={ 'mb-[34px] md:mb-[44px]' }>
                                                <BrandLogo />
                                            </div>

                                            <p className={
                                                clsx(
                                                    'text-[24px] md:text-[36px] mb-[4px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-semibold'
                                                )
                                            }>
                                                { 'You\'ve won' }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[44px] md:text-[64px]',
                                                    'leading-[1.1em]',
                                                    'uppercase',
                                                    'text-center',
                                                    'font-semibold'
                                                )
                                            }>
                                                { variant.spinTheWheelFinalPageText }
                                            </p>

                                            <p className={
                                                clsx(
                                                    'text-[20px]',
                                                    'leading-[1.25em] mt-4',
                                                    'font-light',
                                                    'tracking-wider',
                                                    'text-center',
                                                    'w-[280px]'
                                                )
                                            }>
                                                { 'Check your messages and inbox for your discount!' }
                                            </p>
                                            <div className={
                                                clsx(
                                                    'w-[200px]',
                                                    'flex',
                                                    'flex-col',
                                                    'items-center',
                                                    'gap-2.5',
                                                    'mt-8',
                                                    'fade-in-up'
                                                )
                                            }>
                                                <Button>
                                                    { 'Back to site' }
                                                </Button>
                                            </div>
                                        </Column>
                                    )
                                    : (
                                        <Column >
                                            <div className={ 'mb-[34px] md:mb-[44px]' }>
                                                <BrandLogo />
                                            </div>
                                            <p className={
                                                clsx(
                                                    'text-5xl md:text-6xl',
                                                    'leading-[1.25em] font-bold',
                                                    'tracking-wider uppercase',
                                                    'text-center',
                                                    'w-[400px]'
                                                )
                                            }>
                                                { 'You\'re in' }
                                            </p>
                                            <p className={
                                                clsx(
                                                    'text-[20px]',
                                                    'leading-[1.25em] mt-4',
                                                    'font-light',
                                                    'tracking-wider',
                                                    'text-center',
                                                    'w-[280px]'
                                                )
                                            }>
                                                { 'Check your messages and inbox for your discount!' }
                                            </p>
                                            <div className={
                                                clsx(
                                                    'w-[200px]',
                                                    'flex',
                                                    'flex-col',
                                                    'items-center',
                                                    'gap-2.5',
                                                    'mt-8',
                                                    'fade-in-up'
                                                )
                                            }>
                                                <Button>
                                                    { 'Back to site' }
                                                </Button>
                                            </div>
                                        </Column>
                                    )
                            }

                            { /* Second column for hero image - positioned at bottom on mobile */ }
                            {
                                variant.heroImage
                                    ? (
                                        <Column className={
                                            clsx(
                                                'flex justify-center w-96'
                                            )
                                        }>
                                            {
                                                typeof variant.heroImage === 'function'
                                                    ? (
                                                        <variant.heroImage />
                                                    )
                                                    : (
                                                        <HeroImage
                                                            imageUrl={ variant.heroImage.url }
                                                            alt={ variant.heroImage.alt }
                                                        />
                                                    )
                                            }
                                        </Column>
                                    )
                                    : null
                            }
                        </Columns>
                    </Row>
                </Page>
            </Popup>
            <Teaser
                customText={ variant.teaserText }
                closeButtonPosition={ CLOSE_BUTTON_POSITION.LEFT }
            />
        </PopupContainer>
    );
};
